import React, { useState, useRef } from "react";
import { FiDownload, FiPaperclip, FiExternalLink, FiX } from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import Linkify from "linkify-react";
import { motion, AnimatePresence } from "framer-motion";

function MessageCard({ message, onLikeMessage }) {
  const [showLikeAnimation, setShowLikeAnimation] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const lastTapTime = useRef(0);

  //convert firebase timestamp to date
  const date = message.dateTime.toDate().toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const handleFileDownload = (url, fileName) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "download";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDoubleClick = () => {
    if (!message.isDeleted && onLikeMessage) {
      onLikeMessage(message.id);
      // Show heart animation
      setShowLikeAnimation(true);
      setTimeout(() => setShowLikeAnimation(false), 1000);
    }
  };

  const handleTap = (e) => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTapTime.current;

    // Check if the tap happened within double-tap threshold (300ms)
    if (tapLength < 300 && tapLength > 0) {
      // Double tap detected
      handleDoubleClick();
      // Reset last tap time to prevent triple-tap from triggering another like
      lastTapTime.current = 0;
    } else {
      // Set the timestamp for this tap
      lastTapTime.current = currentTime;
    }
  };

  const handleMediaClick = (e) => {
    // Prevent event from bubbling up to parent elements
    e.stopPropagation();
    setShowMediaModal(true);
  };

  const closeMediaModal = () => {
    setShowMediaModal(false);
  };

  // Custom options for linkify
  const linkifyOptions = {
    defaultProtocol: "https",
    target: "_blank",
    rel: "noopener noreferrer",
    formatHref: {
      email: (href) => `mailto:${href}`,
      url: (href) => href,
    },
    render: {
      url: (props) => <a {...props} className="text-[#363062] underline" />,
    },
  };

  const likeCount = message.likes ? Object.keys(message.likes).length : 0;

  return (
    <div className="flex items-start gap-2 mb-3 group animate-fade-in max-w-[85%]">
      <div className="w-7 h-7 rounded-full bg-[#363062] text-white flex items-center justify-center text-xs flex-shrink-0">
        {message.displayName?.charAt(0).toUpperCase() || "U"}
      </div>
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-1">
          <div className="text-xs font-medium text-[#363062]">
            {message.displayName}
          </div>
        </div>
        <div className="relative">
          {/* Like animation that appears on double click */}
          {showLikeAnimation && (
            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
              <FaHeart className="text-red-500 animate-ping-once w-12 h-12" />
            </div>
          )}

          <div
            className="bg-white rounded-lg rounded-tl-sm px-3 py-2 shadow-sm border border-gray-200 relative"
            onDoubleClick={handleDoubleClick}
            onTouchStart={handleTap}
          >
            {/* If message has an image */}
            {message.fileType === "image" && message.fileUrl && (
              <div
                className="mb-2 bg-white rounded-md overflow-hidden cursor-pointer"
                onClick={handleMediaClick}
              >
                <img
                  src={message.fileUrl}
                  alt="Shared image"
                  className="rounded-md max-w-full max-h-[300px] object-contain bg-white"
                />
              </div>
            )}

            {/* If message has a file */}
            {message.fileType === "file" && message.fileUrl && (
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-md mb-2">
                <div className="w-8 h-8 bg-[#363062]/10 rounded-md flex items-center justify-center">
                  <FiPaperclip className="w-4 h-4 text-[#363062]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">
                    {message.fileName}
                  </p>
                </div>
                <button
                  onClick={() =>
                    handleFileDownload(message.fileUrl, message.fileName)
                  }
                  className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <FiDownload className="w-4 h-4 text-[#363062]" />
                </button>
              </div>
            )}

            {/* If message has a video file */}
            {message.fileType === "video" && message.fileUrl && (
              <div
                className="mb-2 bg-black rounded-md overflow-hidden cursor-pointer"
                onClick={handleMediaClick}
              >
                <video
                  className="rounded-md max-w-full max-h-[300px] object-contain w-full"
                  controls={false}
                  muted
                  playsInline
                  poster={message.thumbnailUrl}
                >
                  <source src={message.fileUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="border-[8px] border-transparent border-l-white ml-1"></span>
                  </div>
                </div>
              </div>
            )}

            {/* If message has a link preview */}
            {message.linkPreview && (
              <div className="mb-2 border border-gray-200 rounded-md overflow-hidden bg-white">
                <a
                  href={message.linkPreview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:bg-gray-50 transition-colors"
                >
                  {message.linkPreview.image && (
                    <div className="w-full h-[160px] bg-white border-b border-gray-100">
                      <img
                        src={message.linkPreview.image}
                        alt={message.linkPreview.title}
                        className="w-full h-full object-cover bg-white"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  )}
                  <div className="p-2 bg-white">
                    <div className="flex items-center text-xs text-gray-500 mb-1">
                      <FiExternalLink className="mr-1 w-3 h-3" />
                      <span>{message.linkPreview.siteName}</span>
                    </div>
                    <p className="font-medium text-sm mb-1 text-gray-800">
                      {message.linkPreview.title}
                    </p>
                    {message.linkPreview.description && (
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {message.linkPreview.description}
                      </p>
                    )}
                  </div>
                </a>
              </div>
            )}

            {/* Message text content */}
            {message.message && (
              <p className="text-gray-800 text-sm">
                <Linkify options={linkifyOptions}>{message.message}</Linkify>
              </p>
            )}

            {/* Like indicator */}
            {likeCount > 0 && (
              <div className="absolute bottom-0 right-0 transform translate-x-1/3 translate-y-1/3">
                <div className="bg-white rounded-full p-1 shadow-sm flex items-center gap-1 border border-gray-200">
                  <FaHeart className="text-red-500 w-3 h-3" />
                  {likeCount > 1 && (
                    <span className="text-xs text-gray-700">{likeCount}</span>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="text-xs text-gray-400 mt-1">{date}</div>
        </div>
      </div>

      {/* Media Modal */}
      <AnimatePresence>
        {showMediaModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="w-full max-w-3xl max-h-[90vh] relative"
            >
              <button
                className="absolute top-2 right-2 bg-white/20 rounded-full p-2 text-white"
                onClick={closeMediaModal}
              >
                <FiX className="w-5 h-5" />
              </button>

              {message.fileType === "image" && (
                <img
                  src={message.fileUrl}
                  alt="Shared media"
                  className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
                />
              )}

              {message.fileType === "video" && (
                <video
                  src={message.fileUrl}
                  controls
                  autoPlay
                  className="w-full h-auto max-h-[90vh] object-contain rounded-lg"
                />
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MessageCard;
